import React, { useState } from "react";
import { Button, Input, Spin, Empty } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CaretLeft } from "@phosphor-icons/react";
import styled from "styled-components";
import { getClientLoyaltyCards } from "../../../service";
import { buildBranchesPath } from "../../../utils/navigation";

// ============================================
// STYLED COMPONENTS
// ============================================

const PageWrapper = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding: 24px 16px 48px;
  min-height: 60vh;
`;

const Title = styled.h2`
  font-size: var(--font-size-heading1);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-heading);
  color: var(--color-text);
  text-align: center;
  margin: 0;
  flex: 1;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  position: relative;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  position: absolute;
  left: -8px;
  z-index: 10;
  
  &:hover {
    background: var(--color-overlay-light);
    color: var(--color-primary);
  }
`;

const Subtitle = styled.p`
  font-size: var(--font-size-body-small);
  line-height: var(--line-height-body);
  color: var(--color-text-tertiary);
  text-align: center;
  margin: 0 0 24px 0;
`;

const PhoneForm = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 32px;

  .ant-input {
    height: 44px;
    border-radius: 12px;
    font-size: var(--font-size-body-medium);
  }

  .ant-btn {
    height: 44px;
    border-radius: 12px;
    font-weight: 600;
  }
`;

const CardWrapper = styled.div<{ $color?: string }>`
  background: ${(p) => p.$color || "#e11d48"};
  border-radius: 16px;
  padding: 20px;
  color: var(--color-text-inverse);
  margin-bottom: 16px;
  position: relative;
  overflow: hidden;
  box-shadow: var(--shadow-card);

  &::before {
    content: "";
    position: absolute;
    top: -30px;
    right: -30px;
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -20px;
    left: -20px;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.07);
  }
`;

const CardName = styled.h3`
  font-size: var(--font-size-heading2);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-heading-tight);
  margin: 0 0 4px 0;
  position: relative;
  z-index: 1;
`;

const CardDescription = styled.p`
  font-size: var(--font-size-body-small);
  line-height: var(--line-height-body);
  opacity: 0.85;
  margin: 0 0 16px 0;
  position: relative;
  z-index: 1;
`;

const StampsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
  position: relative;
  z-index: 1;
`;

const StampDot = styled.div<{ $filled: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-body-medium);
  border: 2px solid rgba(255, 255, 255, ${(p) => (p.$filled ? "0.9" : "0.3")});
  background: ${(p) =>
        p.$filled ? "rgba(255, 255, 255, 0.25)" : "transparent"};
  transition: all 0.3s ease;
  
  ${(p) =>
        p.$filled &&
        `
    animation: stampPop 0.3s ease-out;
  `}

  @keyframes stampPop {
    0% { transform: scale(0.5); opacity: 0; }
    70% { transform: scale(1.2); }
    100% { transform: scale(1); opacity: 1; }
  }
`;

const ProgressText = styled.div`
  font-size: var(--font-size-body-small);
  line-height: var(--line-height-body);
  opacity: 0.8;
  text-align: right;
  position: relative;
  z-index: 1;
`;

const RewardBadge = styled.div`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  padding: 12px 16px;
  margin-top: 12px;
  position: relative;
  z-index: 1;
  backdrop-filter: blur(4px);
`;

const RewardTitle = styled.div`
  font-size: var(--font-size-body-small);
  line-height: var(--line-height-body);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.8;
  margin-bottom: 4px;
`;

const CouponCode = styled.div`
  font-size: var(--font-size-body-large);
  font-weight: var(--font-weight-bold);
  font-family: monospace;
  letter-spacing: 2px;
`;

const RewardExpiry = styled.div`
  font-size: var(--font-size-body-small);
  line-height: var(--line-height-body);
  opacity: 0.7;
  margin-top: 4px;
`;

const GreetingText = styled.p`
  font-size: var(--font-size-body-medium);
  line-height: var(--line-height-body);
  color: var(--color-text-secondary);
  text-align: center;
  margin: 0 0 20px 0;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

// ============================================
// COMPONENT
// ============================================

export const LoyaltyPage: React.FC = () => {
    const [phone, setPhone] = useState("");
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [searched, setSearched] = useState(false);
    const navigate = useNavigate();
    const { restaurantSlug } = useParams();
    const { t } = useTranslation();

    const handleBack = () => {
        navigate(buildBranchesPath(restaurantSlug || null));
    };

    const handleSearch = async () => {
        if (!phone || phone.length < 9) return;

        setLoading(true);
        setError(null);
        setSearched(true);

        try {
            const result = await getClientLoyaltyCards(phone);
            setData(result);
        } catch (err: any) {
            if (err?.response?.status === 400) {
                setError(t("loyaltyPage.errorInvalidPhone"));
            } else {
                setError(t("loyaltyPage.errorFetchFailed"));
            }
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr: string) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString("pl-PL", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    };

    return (
        <PageWrapper>
            <Header>
                <BackButton onClick={handleBack}>
                    <CaretLeft size={24} weight="bold" />
                </BackButton>
                <Title>{t("loyaltyPage.title")}</Title>
            </Header>
            <Subtitle>
                {t("loyaltyPage.subtitle")}
            </Subtitle>

            <PhoneForm>
                <Input
                    placeholder="+48 501 234 567"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    onPressEnter={handleSearch}
                    type="tel"
                    size="large"
                />
                <Button
                    type="primary"
                    onClick={handleSearch}
                    loading={loading}
                    style={{
                        background: "var(--color-primary)",
                        borderColor: "var(--color-primary)",
                    }}
                >
                    {t("loyaltyPage.searchButton")}
                </Button>
            </PhoneForm>

            {loading && (
                <LoadingWrapper>
                    <Spin size="large" />
                </LoadingWrapper>
            )}

            {error && (
                <Empty
                    description={error}
                    style={{ marginTop: 40 }}
                />
            )}

            {data && !loading && (
                <div>
                    {data.client && (
                        <GreetingText>
                            {data.client.name
                                ? t("loyaltyPage.greetingNamed", { name: data.client.name })
                                : t("loyaltyPage.greeting")}
                        </GreetingText>
                    )}

                    {(!data.cards || data.cards.length === 0) && searched && (
                        <Empty
                            description={t("loyaltyPage.emptyNoCards")}
                            style={{ marginTop: 40 }}
                        />
                    )}

                    {data.cards?.map((clc: any) => {
                        const card = clc.loyaltyCard;
                        if (!card) return null;

                        const stamps = clc.currentStamps || 0;
                        const required = card.stampsRequired || 10;
                        const stampDots = [];

                        for (let i = 0; i < required; i++) {
                            stampDots.push(
                                <StampDot key={i} $filled={i < stamps}>
                                    {i < stamps ? "★" : "☆"}
                                </StampDot>
                            );
                        }

                        return (
                            <CardWrapper key={clc.id} $color={card.cardColor}>
                                <CardName>{card.name}</CardName>
                                {card.description && (
                                    <CardDescription>{card.description}</CardDescription>
                                )}

                                <StampsGrid>{stampDots}</StampsGrid>

                                <ProgressText>
                                    {t("loyaltyPage.progressStamps", { stamps, required })}
                                    {stamps >= required && t("loyaltyPage.progressCompleted")}
                                </ProgressText>

                                {card.rewardDescription && stamps < required && (
                                    <CardDescription style={{ marginTop: 8, marginBottom: 0 }}>
                                        {t("loyaltyPage.rewardLine", { reward: card.rewardDescription })}
                                    </CardDescription>
                                )}

                                {/* Show available rewards (unredeemed coupons) */}
                                {clc.rewards?.map((reward: any) => (
                                    <RewardBadge key={reward.id}>
                                        <RewardTitle>{t("loyaltyPage.rewardTitle")}</RewardTitle>
                                        {reward.couponCode && (
                                            <CouponCode>{reward.couponCode}</CouponCode>
                                        )}
                                        {card.rewardDescription && (
                                            <div style={{ fontSize: 'var(--font-size-body-small)', marginTop: 4 }}>
                                                {card.rewardDescription}
                                            </div>
                                        )}
                                        {reward.expiresAt && (
                                            <RewardExpiry>
                                                {t("loyaltyPage.expiresOn", { date: formatDate(reward.expiresAt) })}
                                            </RewardExpiry>
                                        )}
                                    </RewardBadge>
                                ))}
                            </CardWrapper>
                        );
                    })}
                </div>
            )}
        </PageWrapper>
    );
};
